import { environment } from '../env/environment';

const APP_SOURCE = 'client';
let sessionId = null;
let stepStartTime = null;
let currentEmail = null;
let currentPatientName = null;
let currentTenantId = null;
let currentSubmissionId = null;
let abandonListener = null;
let currentStep = null;
let currentStepName = null;
let isSubmitting = false;

const getSessionId = () => {
  if (!sessionId) {
    sessionId = 'cs_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
  }
  return sessionId;
};

const getDeviceInfo = () => ({
  userAgent: navigator.userAgent,
  platform: navigator.platform,
  screen: `${window.screen.width}x${window.screen.height}`,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  dpr: window.devicePixelRatio || 1,
  language: navigator.language,
  online: navigator.onLine,
  connection: navigator.connection?.effectiveType || 'unknown'
});

const send = async (eventType, data = {}) => {
  try {
    const payload = {
      tenantId: currentTenantId,
      submissionId: currentSubmissionId,
      sessionId: getSessionId(),
      email: currentEmail,
      patientName: currentPatientName,
      appSource: APP_SOURCE,
      eventType,
      stepNumber: data.stepNumber || currentStep,
      stepName: data.stepName || currentStepName,
      errorMessage: data.errorMessage || null,
      errorDetails: data.errorDetails || null,
      deviceInfo: getDeviceInfo(),
      durationMs: data.durationMs || null
    };

    if (navigator.sendBeacon) {
      navigator.sendBeacon(
        `${environment.baseUrl}/api/activity-log`,
        new Blob([JSON.stringify(payload)], { type: 'application/json' })
      );
    } else {
      fetch(`${environment.baseUrl}/api/activity-log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        keepalive: true
      }).catch(() => {});
    }
  } catch (e) {
    // Never let logging break the app
  }
};

const activityLogger = {
  setContext({ email, patientName, tenantId, submissionId }) {
    if (email) currentEmail = email;
    if (patientName) currentPatientName = patientName;
    if (tenantId) currentTenantId = tenantId;
    if (submissionId) currentSubmissionId = submissionId;
  },

  formStarted() {
    if (abandonListener) {
      window.removeEventListener('beforeunload', abandonListener);
      abandonListener = null;
    }
    sessionId = null;
    currentStep = null;
    currentStepName = null;
    stepStartTime = null;
    send('form_started');
    abandonListener = () => {
      if (isSubmitting) {
        send('submit_interrupted', {
          stepNumber: currentStep,
          stepName: currentStepName,
          errorMessage: 'Page closed/crashed during submission'
        });
      } else {
        send('form_abandoned', {
          stepNumber: currentStep,
          stepName: currentStepName,
          durationMs: stepStartTime ? Date.now() - stepStartTime : null
        });
      }
    };
    window.addEventListener('beforeunload', abandonListener);
  },

  stepStarted(stepNumber, stepName) {
    currentStep = stepNumber;
    currentStepName = stepName;
    stepStartTime = Date.now();
    send('step_started', { stepNumber, stepName });
  },

  stepCompleted(stepNumber, stepName) {
    const duration = stepStartTime ? Date.now() - stepStartTime : null;
    send('step_completed', { stepNumber, stepName, durationMs: duration });
    stepStartTime = Date.now();
  },

  stepValidationError(stepNumber, stepName, errors) {
    send('step_validation_error', {
      stepNumber, stepName,
      errorMessage: `Validation failed: ${Object.keys(errors).join(', ')}`,
      errorDetails: { fields: Object.keys(errors), messages: errors }
    });
  },

  stepSaveError(stepNumber, stepName, error) {
    send('step_save_error', {
      stepNumber, stepName,
      errorMessage: error.message || String(error),
      errorDetails: { status: error.status, response: error.response }
    });
  },

  submitStarted() {
    isSubmitting = true;
    send('submit_started');
  },

  submitPdfError(error) {
    isSubmitting = false;
    send('submit_pdf_error', {
      errorMessage: error.message || 'PDF generation failed',
      errorDetails: { stack: error.stack?.slice(0, 500) }
    });
  },

  submitApiError(error) {
    isSubmitting = false;
    send('submit_api_error', {
      errorMessage: error.message || 'Backend submission failed',
      errorDetails: { stack: error.stack?.slice(0, 500), status: error.status }
    });
  },

  submitCompleted() {
    isSubmitting = false;
    send('submit_completed');
    if (abandonListener) {
      window.removeEventListener('beforeunload', abandonListener);
      abandonListener = null;
    }
    sessionId = null;
  },

  networkError(stepNumber, stepName, error) {
    send('network_error', {
      stepNumber, stepName,
      errorMessage: error.message || 'Network error',
      errorDetails: { online: navigator.onLine, type: error.name }
    });
  },

  browserError(error) {
    send('browser_error', {
      errorMessage: error.message || 'Uncaught error',
      errorDetails: { stack: error.stack?.slice(0, 500), source: error.filename, line: error.lineno }
    });
  }
};

export default activityLogger;
